import { Component, Inject, OnInit } from '@angular/core';
import { formatCpfCnpj, isValidCpfCnpj, normalizeDocumentForApi } from '@app-utils/document-utils';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SearchDealer } from '@app-model/oem-dealer';
import { PageControl } from '@app-model/page-control';
import { BidStatus } from '@app-services/dealer.service';
import { DealerInfo, DirectSaleRequest, OemDealerService } from '@app-services/oem-dealer.service';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import { FastQuoteService } from '@app-services/fast-quote.service';
import { ProposalMarginLimitHelper } from '@app-model/proposal-margin.model';
import {
  belowMinimumMarginMessage,
  formatMarginPercent,
  getProposalMargin,
  isBelowUserMarginLimit,
} from '@app-utils/proposal-margin.utils';

export type FastQuoteDirectSaleSource = 'evaluation' | 'tradeIn';

export interface DialogDirectSaleData {
  vehicleId: string;
  oemDealerId: string;
  costPrice?: number | null;
  fastQuoteSource?: FastQuoteDirectSaleSource;
  canAcceptBelowMinimumMargin?: boolean;
}

export type DirectSaleMode = 'FINISH_NOW' | 'KEEP_OPEN';

export interface DirectSaleKeepOpenOption {
  label: string;
  minutes: number;
  icon: string;
}

export interface DialogDirectSaleResult {
  vehicleId: string;
  dealerId: string;
  proposalValue: number;
  mode: DirectSaleMode;
  keepOpenMinutes?: number;
}

@Component({
  selector: 'app-dialog-direct-sale',
  templateUrl: './dialog-direct-sale.component.html',
  styleUrls: ['./dialog-direct-sale.component.scss']
})
export class DialogDirectSaleComponent implements OnInit {
  cpfCnpj = '';
  phoneNumber = '';
  phoneInvalid = false;
  proposalValue: number;
  dealer: DealerInfo;
  dealerOptions: DealerInfo[] = [];
  notFound = false;
  lastSearch: 'cpfCnpj' | 'phone' = 'cpfCnpj';
  loading = false;
  submitting = false;
  errorMessage: string;
  private marginLimit: ProposalMarginLimitHelper | null = null;

  mode: DirectSaleMode = null;
  keepOpenMinutes: number = null;

  readonly keepOpenOptions: DirectSaleKeepOpenOption[] = [
    { label: '10 minutos', minutes: 10,  icon: 'timer' },
    { label: '30 minutos', minutes: 30,  icon: 'timer' },
    { label: '1 hora',     minutes: 60,  icon: 'schedule' },
    { label: '3 horas',    minutes: 180, icon: 'schedule' },
  ];

  constructor(
    private dialogRef: MatDialogRef<DialogDirectSaleComponent>,
    private oemDealerService: OemDealerService,
    private toast: ToastrService,
    private fastQuoteService: FastQuoteService,
    @Inject(MAT_DIALOG_DATA) public data: DialogDirectSaleData
  ) {}

  ngOnInit(): void {
    if (this.data?.oemDealerId) {
      this.oemDealerService.getProposalMarginLimit(this.data.oemDealerId).subscribe({
        next: (limit) => {
          this.marginLimit = limit;
        },
      });
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  get proposalMargin(): number | undefined {
    return getProposalMargin(this.proposalValue, this.data?.costPrice);
  }

  get userLimitLabel(): string | null {
    const limit = this.marginLimit?.effectiveLimit;
    if (limit == null) {
      return null;
    }
    return formatMarginPercent(limit);
  }

  get isMarginBlocked(): boolean {
    const min = this.marginLimit?.minimumProposalAcceptanceMargin ?? null;
    const limit = this.marginLimit?.effectiveLimit ?? null;
    const canAcceptBelow = this.data?.canAcceptBelowMinimumMargin ?? false;
    return isBelowUserMarginLimit(this.proposalValue, this.data?.costPrice, min, limit, canAcceptBelow);
  }

  onCpfCnpjChange(): void {
    this.errorMessage = undefined;
    this.notFound = false;
    this.clearDealerResult();
  }

  onPhoneChange(): void {
    this.errorMessage = undefined;
    this.phoneInvalid = false;
    this.notFound = false;
    this.clearDealerResult();
  }

  selectMode(mode: DirectSaleMode): void {
    this.mode = mode;
    if (mode === 'FINISH_NOW') {
      this.keepOpenMinutes = null;
    }
    this.errorMessage = undefined;
  }

  selectKeepCurrentSchedule(): void {
    this.mode = 'KEEP_OPEN';
    this.keepOpenMinutes = null;
    this.errorMessage = undefined;
  }

  selectKeepOpen(minutes: number): void {
    this.mode = 'KEEP_OPEN';
    this.keepOpenMinutes = this.keepOpenMinutes === minutes ? null : minutes;
    this.errorMessage = undefined;
  }

  isKeepOpenSelected(minutes: number): boolean {
    return this.mode === 'KEEP_OPEN' && this.keepOpenMinutes === minutes;
  }

  get isKeepCurrentScheduleSelected(): boolean {
    return this.mode === 'KEEP_OPEN' && this.keepOpenMinutes == null;
  }

  searchDealer(): void {
    const cpfCnpjNumber = this.getCpfCnpjNumber();
    if (!cpfCnpjNumber) {
      this.errorMessage = 'Informe um CPF/CNPJ válido para buscar o comprador.';
      this.notFound = false;
      this.clearDealerResult();
      return;
    }

    const payload = this.buildSearchPayload();
    payload.cpfCnpj = cpfCnpjNumber;
    this.lastSearch = 'cpfCnpj';
    this.executeDealerSearch(payload, true);
  }

  searchDealerByPhone(): void {
    const phoneNumber = this.getPhoneNumber();
    if (!phoneNumber) {
      this.phoneInvalid = true;
      this.errorMessage = 'Informe um telefone com DDD válido para buscar o comprador.';
      this.notFound = false;
      this.clearDealerResult();
      return;
    }

    this.phoneInvalid = false;
    const payload = this.buildSearchPayload();
    payload.phoneNumber = phoneNumber;
    payload.pageControl = new PageControl();
    payload.pageControl.page = 0;
    payload.pageControl.pageSize = 100;
    this.lastSearch = 'phone';
    this.executeDealerSearch(payload, false);
  }

  selectDealer(dealer: DealerInfo): void {
    this.dealer = dealer;
    this.errorMessage = undefined;
    this.notFound = false;
  }

  private buildSearchPayload(): SearchDealer {
    const payload = new SearchDealer();
    payload.oemDealerId = this.data?.oemDealerId;
    payload.addDealers = true;
    return payload;
  }

  private executeDealerSearch(payload: SearchDealer, singleResult: boolean): void {
    this.loading = true;
    this.errorMessage = undefined;
    this.notFound = false;
    this.clearDealerResult();

    this.oemDealerService.searchDealers(payload, true).subscribe({
      next: (response) => {
        this.applySearchResult(response?.data ?? [], singleResult);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.notFound = false;
        this.errorMessage = 'Não foi possível buscar o comprador no momento. Tente novamente.';
      }
    });
  }

  private applySearchResult(data: DealerInfo[], singleResult: boolean): void {
    if (singleResult || data.length <= 1) {
      this.dealerOptions = [];
      this.dealer = data.length ? data[0] : undefined;
    } else {
      this.dealerOptions = data;
      this.dealer = undefined;
    }
    this.notFound = !this.dealer && this.dealerOptions.length === 0;
  }

  private clearDealerResult(): void {
    this.dealer = undefined;
    this.dealerOptions = [];
  }

  get canSubmit(): boolean {
    if (!this.dealer || this.isDealerBlocked) {
      return false;
    }
    if (!this.proposalValue || this.proposalValue <= 0) {
      return false;
    }
    if (this.isMarginBlocked) {
      return false;
    }
    if (this.mode === 'FINISH_NOW' || this.mode === 'KEEP_OPEN') {
      return true;
    }
    return false;
  }

  submitProposal(): void {
    if (!this.dealer?.id) {
      this.errorMessage = 'Busque um comprador válido antes de continuar.';
      return;
    }
    if (this.isDealerBlocked) {
      this.errorMessage = this.dealerBlockedMessage;
      return;
    }
    if (!this.proposalValue || this.proposalValue <= 0) {
      this.errorMessage = 'Informe um valor de proposta válido.';
      return;
    }
    if (!this.mode) {
      this.errorMessage = 'Selecione o que deseja fazer após registrar a proposta.';
      return;
    }
    if (!this.data?.vehicleId) {
      this.errorMessage = 'Não foi possível identificar o veículo para registrar a proposta.';
      return;
    }
    if (this.isMarginBlocked) {
      this.errorMessage = belowMinimumMarginMessage(this.marginLimit?.effectiveLimit);
      return;
    }

    this.submitting = true;
    this.errorMessage = undefined;

    const result: DialogDirectSaleResult = {
      vehicleId: this.data?.vehicleId,
      dealerId: this.dealer.id,
      proposalValue: Math.trunc(this.proposalValue),
      mode: this.mode,
      ...(this.mode === 'KEEP_OPEN' && this.keepOpenMinutes != null && { keepOpenMinutes: this.keepOpenMinutes }),
    };

    const payload: DirectSaleRequest = {
      dealerId: result.dealerId,
      proposalValue: result.proposalValue,
      mode: result.mode,
      ...(this.keepOpenMinutes != null && { keepOpenMinutes: this.keepOpenMinutes }),
    };

    const request$ = this.data.fastQuoteSource === 'evaluation'
      ? this.fastQuoteService.directSaleFromEvaluation(this.data.vehicleId, payload)
      : this.data.fastQuoteSource === 'tradeIn'
        ? this.fastQuoteService.directSaleFromTradeIn(this.data.vehicleId, payload)
        : this.oemDealerService.directSale(this.data.vehicleId, payload);

    request$
      .pipe(finalize(() => this.submitting = false))
      .subscribe((response) => {
        if (!response?.success) {
          if (response?.status === 'BELOW_MINIMUM_MARGIN') {
            this.errorMessage = belowMinimumMarginMessage(response.effectiveMarginLimit);
          } else {
            this.errorMessage = BidStatus[response?.status] || 'Não foi possível registrar a proposta de venda direta.';
          }
          this.toast.error(this.errorMessage);
          this.dialogRef.close(null);
          return;
        }

        this.toast.success(response?.message || 'Proposta de venda direta registrada com sucesso.');
        this.dialogRef.close(result);
      });
  }

  get phoneNumbersDisplay(): string {
    if (!this.dealer?.phoneNumbers?.length) {
      return '-';
    }
    return this.dealer.phoneNumbers.join(', ');
  }

  get emailDisplay(): string {
    if (!this.dealer) {
      return '-';
    }
    if (this.dealer.email) {
      return this.dealer.email;
    }
    if (this.dealer.emails?.length) {
      return this.dealer.emails[0];
    }
    return '-';
  }

  get cpfCnpjDisplay(): string {
    return formatCpfCnpj(this.dealer?.cpfCnpj) || this.dealer?.cpfCnpj || '-';
  }

  get isDealerBlocked(): boolean {
    return !!this.dealer && (!!this.dealer.inactive || !!this.dealer.hasLatePayment);
  }

  get dealerBlockedMessage(): string | undefined {
    if (this.dealer?.inactive) {
      return 'Este comprador está inativado ou bloqueado na plataforma. Não é possível registrar proposta de venda direta para ele no momento.';
    }
    if (this.dealer?.hasLatePayment) {
      return 'Este comprador possui pendências financeiras. Não é possível registrar proposta de venda direta para ele no momento.';
    }
    return undefined;
  }

  get notFoundMessage(): string {
    if (this.lastSearch === 'phone') {
      return 'Nenhum comprador cadastrado foi encontrado com o telefone informado.';
    }
    return 'Nenhum comprador cadastrado foi encontrado com o CPF/CNPJ informado.';
  }

  formatDocument(value: string): string {
    return formatCpfCnpj(value) || value || '-';
  }

  private getCpfCnpjNumber(): string | undefined {
    const doc = normalizeDocumentForApi(this.cpfCnpj);
    if (!isValidCpfCnpj(doc)) {
      return undefined;
    }
    return doc;
  }

  private getPhoneNumber(): string | undefined {
    const digits = (this.phoneNumber || '').replace(/\D/g, '');
    const isCellPhone = digits.length === 11 && digits.charAt(2) === '9';
    const isLandline = digits.length === 10;
    if (!isCellPhone && !isLandline) {
      return undefined;
    }
    return digits;
  }
}
